import { useMemo } from "react";

import { ROW_HEIGHT_HEADER, ROW_HEIGHT_TRACK } from "../constants";
import { useTimelineStore } from "../store/timeline-context";
import type { TRenderableRow, TSegment, TTimelineItem } from "../types";

type UseTimelineRowsProps = {
  items: TTimelineItem[];
};

const SYNTHETIC_PREFIX = "__bare__";

function isSegment(node: TTimelineItem): node is TSegment {
  return node.type === "clip" || node.type === "point_box" || node.type === "point" || node.type === "polygon";
}

function syntheticBareRow(segment: TSegment, depth: number, y: number): TRenderableRow {
  return {
    id: `${SYNTHETIC_PREFIX}${segment.id}`,
    label: segment.label ?? "",
    kind: "bare-segment",
    depth,
    data: segment,
    y,
    height: ROW_HEIGHT_TRACK,
  };
}

/**
 * Emission rules:
 *   - Track → one row at depth D. Atomic — spatial children live as waypoints,
 *     not rows.
 *   - Collection → one header row at depth D; recurse at depth D+1 if expanded.
 *   - Bare segment (clip / point_box / point / polygon) → one synthetic track
 *     row at depth D wrapping the segment.
 *   - Video is rendered by TimelineEditor separately and never reaches here.
 */
function flattenNode(
  node: TTimelineItem,
  depth: number,
  collapsedCollections: Set<string>,
  startY: number,
): { rows: TRenderableRow[]; consumedY: number } {
  // Tracks are atomic leaves: one row per track, no recursion.
  if (node.type === "track") {
    return {
      rows: [
        {
          id: node.id,
          label: node.label ?? "",
          kind: "track",
          depth,
          data: node,
          y: startY,
          height: ROW_HEIGHT_TRACK,
        },
      ],
      consumedY: startY + ROW_HEIGHT_TRACK,
    };
  }

  if (node.type === "collection") {
    const isCollapsed = collapsedCollections.has(node.id);
    const rows: TRenderableRow[] = [
      {
        id: node.id,
        label: node.label ?? "",
        kind: "collection-header",
        depth,
        isCollapsed,
        data: node,
        y: startY,
        height: ROW_HEIGHT_HEADER,
      },
    ];
    let y = startY + ROW_HEIGHT_HEADER;

    if (!isCollapsed) {
      for (const child of node.children) {
        if (isSegment(child)) {
          rows.push(syntheticBareRow(child, depth + 1, y));
          y += ROW_HEIGHT_TRACK;
          continue;
        }
        const nested = flattenNode(child, depth + 1, collapsedCollections, y);
        rows.push(...nested.rows);
        y = nested.consumedY;
      }
    }

    return { rows, consumedY: y };
  }

  // Bare segment at root — synthetic track row.
  if (isSegment(node)) {
    return {
      rows: [syntheticBareRow(node, depth, startY)],
      consumedY: startY + ROW_HEIGHT_TRACK,
    };
  }

  // TVideo or anything unexpected: skip (video is rendered elsewhere).
  return { rows: [], consumedY: startY };
}

export const useTimelineRows = ({ items }: UseTimelineRowsProps) => {
  const collapsedCollections = useTimelineStore((state) => state.collapsedCollections);
  const toggleCollapsed = useTimelineStore((state) => state.toggleCollapsed);

  const { renderableRows, totalHeight } = useMemo(() => {
    const rows: TRenderableRow[] = [];
    let y = 0;
    for (const item of items) {
      const { rows: emitted, consumedY } = flattenNode(item, 0, collapsedCollections, y);
      rows.push(...emitted);
      y = consumedY;
    }
    return { renderableRows: rows, totalHeight: y };
  }, [items, collapsedCollections]);

  return {
    renderableRows,
    totalHeight,
    toggleCollapsed,
  };
};
